import { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Bell, Gear, BoxArrowRight, PersonCircle } from "react-bootstrap-icons";
import { useAuth } from "../contexts/AuthContext";

const AUTO_CLOSE_DELAY = 5000;

export default function SellerTopBar() {
  const { auth, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [showNotifications, setShowNotifications] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [unreadCount, setUnreadCount] = useState(3); // demo value

  const notificationRef = useRef(null);
  const userMenuRef = useRef(null);
  const timerRef = useRef(null);

  /* ---------------- AUTO CLOSE ---------------- */
  const startAutoClose = () => {
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      setShowNotifications(false);
      setShowUserMenu(false);
    }, AUTO_CLOSE_DELAY);
  };

  const stopAutoClose = () => {
    clearTimeout(timerRef.current);
  };
  const goToSettings = () => {
    setShowUserMenu(false); // close dropdown if needed
    navigate("/dashboard/settings");
  };

  useEffect(() => {
    if (showNotifications || showUserMenu) {
      startAutoClose();
    }
    return stopAutoClose;
  }, [showNotifications, showUserMenu]);

  /* ---------------- CLICK OUTSIDE ---------------- */
  useEffect(() => {
    function handleClickOutside(e) {
      if (
        !notificationRef.current?.contains(e.target) &&
        !userMenuRef.current?.contains(e.target)
      ) {
        setShowNotifications(false);
        setShowUserMenu(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  /* ---------------- CLOSE ON ROUTE CHANGE ---------------- */
  useEffect(() => {
    setShowNotifications(false);
    setShowUserMenu(false);
  }, [location.pathname]);

  return (
    <header
      className="bg-white border-bottom sticky-top"
      style={{ zIndex: 1000 }}
    >
      <div
        className="d-flex align-items-center justify-content-between px-3 px-md-4"
        style={{ height: 56 }}
      >
        {/* LOGO */}
        <div className="fw-bold fs-6 fs-md-5">Seller Center</div>

        {/* ACTIONS */}
        <div className="d-flex align-items-center gap-3">
          {/* ================= NOTIFICATIONS ================= */}
          <div
            className="position-relative"
            ref={notificationRef}
            onMouseEnter={stopAutoClose}
            onMouseLeave={startAutoClose}
          >
            <button
              className="btn btn-light p-2 position-relative"
              onClick={() => {
                setShowNotifications((v) => !v);
                setShowUserMenu(false);
                setUnreadCount(0); // mark as read
              }}
            >
              <Bell size={18} />

              {/* UNREAD BADGE */}
              {unreadCount > 0 && (
                <span
                  className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger"
                  style={{ fontSize: 10 }}
                >
                  {unreadCount}
                </span>
              )}
            </button>

            {showNotifications && (
              <div
                className="dropdown-panel animate-dropdown"
                style={{ width: "90vw", maxWidth: 320 }}
              >
                <div className="p-3 border-bottom fw-semibold">
                  Notifications
                </div>

                <ul className="list-group list-group-flush">
                  <li className="list-group-item">📩 New message from buyer</li>
                  <li className="list-group-item">💸 Refund requested</li>
                  <li className="list-group-item">⚠️ Stock running low</li>
                </ul>

                <div className="text-center p-2">
                  <small className="text-muted">View all</small>
                </div>
              </div>
            )}
          </div>

          {/* ================= USER MENU ================= */}
          <div
            className="position-relative"
            ref={userMenuRef}
            onMouseEnter={stopAutoClose}
            onMouseLeave={startAutoClose}
          >
            <button
              className="btn btn-light d-flex align-items-center gap-2 p-2"
              onClick={() => {
                setShowUserMenu((v) => !v);
                setShowNotifications(false);
              }}
            >
              <div
                className="rounded-circle bg-secondary text-white d-flex align-items-center justify-content-center"
                style={{ width: 32, height: 32 }}
              >
                {auth?.fullName?.[0] || <PersonCircle />}
              </div>

              <span className="d-none d-md-inline fw-medium">
                {auth?.fullName || "User"}
              </span>
            </button>

            {showUserMenu && (
              <div
                className="dropdown-panel animate-dropdown"
                style={{ width: 220 }}
              >
                <button
                  className="dropdown-item d-flex align-items-center gap-2 p-2
                "
                  onClick={goToSettings}
                >
                  <Gear /> Settings
                </button>

                <hr className="my-1" />

                <button
                  className="dropdown-item d-flex align-items-center gap-2 p-2 text-danger"
                  onClick={logout}
                >
                  <BoxArrowRight /> Logout
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ================= ANIMATION STYLES ================= */}
      <style>
        {`
          .dropdown-panel {
            position: absolute;
            right: 0;
            margin-top: 8px;
            background: #fff;
            border: 1px solid #dee2e6;
            border-radius: 8px;
            box-shadow: 0 8px 24px rgba(0,0,0,.12);
            z-index: 1050;
          }

          .animate-dropdown {
            animation: fadeSlideIn 0.2s ease-out;
          }

          @keyframes fadeSlideIn {
            from {
              opacity: 0;
              transform: translateY(-6px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
        `}
      </style>
    </header>
  );
}
